import { useState } from "react";
import { useAppData } from "@/data/AppDataContext";
import { useOnboarding } from "@/data/contractor/onboarding/OnboardingProvider";
import { getOnboardingLifecycleRows } from "@/data/contractor/onboarding/lifecycle";
import { getContractorComplianceListStatus } from "@/data/contractor/compliance/contractorComplianceListStatus";
import { Card, CardContent } from "@/components/ui/card";
import { StatusBadge } from "@/components/StatusBadge";
import { computeScorecard } from "@/hooks/useContractorScorecards";
import { ReliabilityBadge } from "@/components/ReliabilityBadge";
import {
  Star, Phone, Mail, MapPin, Wrench, ChevronDown, ChevronUp,
  Search, Shield, FileText, Clock, Briefcase, CheckCircle, XCircle,
  DollarSign, Eye, AlertTriangle,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Link } from "react-router-dom";
import { BranchRegionFilter, EMPTY_BR, type BranchRegionState } from "@/components/BranchRegionFilter";
import { QuickViews, type QuickView } from "@/components/QuickViews";
import { EmptyState } from "@/components/EmptyState";

type FilterTab = "all" | "active" | "inactive" | "pending-response" | "high-rated" | "compliance-risk";

function RatingStars({ rating, size = "h-3 w-3" }: { rating: number; size?: string }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star key={s} className={`${size} ${s <= Math.round(rating) ? "text-warning fill-warning" : "text-muted/40"}`} />
      ))}
      <span className="text-xs text-muted-foreground ml-1">{rating}</span>
    </div>
  );
}

export default function ContractorsList() {
  const { contractors, opportunities, jobs, issues, credits } = useAppData();
  const onboarding = useOnboarding();
  const [filter, setFilter] = useState<FilterTab>("all");
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [brFilter, setBrFilter] = useState<BranchRegionState>(EMPTY_BR);
  const withdrawnContractorIds = new Set(
    getOnboardingLifecycleRows(onboarding.inviteTokens, onboarding.sessions)
      .filter((row) => row.stage === "withdrawn" && row.contractorId)
      .map((row) => row.contractorId as string),
  );

  // Pre-filter by branch/region first
  const brFiltered = contractors.filter((c) => {
    if (withdrawnContractorIds.has(c.id)) return false;
    if (brFilter.branch !== "all" && c.branch !== brFilter.branch) return false;
    if (brFilter.region !== "all" && !c.serviceRegions.includes(brFilter.region)) return false;
    return true;
  });

  const active = brFiltered.filter((c) => c.active);
  const inactive = brFiltered.filter((c) => !c.active);
  const contractorById = new Map(contractors.map((contractor) => [contractor.id, contractor]));
  const getCompliance = (contractor: (typeof contractors)[number]) =>
    getContractorComplianceListStatus(
      contractor,
      onboarding.complianceDocuments,
      onboarding.sessions,
      contractor.parentEntityId ? contractorById.get(contractor.parentEntityId) : undefined,
    );

  const pendingResponse = brFiltered.filter((c) => c.active && opportunities.some((o) => o.contractorId === c.id && (o.responseStatus === "Pending" || o.responseStatus === "No Response")));
  const highRated = brFiltered.filter((c) => c.active && c.rating >= 4.5);
  const complianceRisk = brFiltered.filter((c) => c.active && getCompliance(c).tone !== "ok");

  const contractorQuickViews: QuickView[] = [
    { key: "all", label: "All", count: brFiltered.length },
    { key: "active", label: "Active", count: active.length, accent: "success" },
    { key: "pending-response", label: "Pending Response", count: pendingResponse.length, accent: "warning" },
    { key: "high-rated", label: "Top Rated", count: highRated.length, accent: "info" },
    { key: "compliance-risk", label: "Compliance Risk", count: complianceRisk.length, accent: "destructive" },
    { key: "inactive", label: "Inactive", count: inactive.length },
  ];

  const filtered = brFiltered.filter((c) => {
    if (filter === "active" && !c.active) return false;
    if (filter === "inactive" && c.active) return false;
    if (filter === "pending-response" && !pendingResponse.includes(c)) return false;
    if (filter === "high-rated" && !highRated.includes(c)) return false;
    if (filter === "compliance-risk" && !complianceRisk.includes(c)) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        c.companyName.toLowerCase().includes(q) ||
        c.contactName.toLowerCase().includes(q) ||
        c.tradeType.toLowerCase().includes(q) ||
        c.serviceArea.toLowerCase().includes(q)
      );
    }
    return true;
  });

  // Network stats
  const totalOpps = opportunities.length;
  const totalWon = opportunities.filter((o) => o.isWinner).length;
  const avgResponseTime = (() => {
    const responded = opportunities.filter((o) => o.responseDate && o.sentDate);
    if (responded.length === 0) return "—";
    const totalDays = responded.reduce((sum, o) => {
      const sent = new Date(o.sentDate).getTime();
      const resp = new Date(o.responseDate).getTime();
      return sum + Math.max(0, (resp - sent) / (1000 * 60 * 60 * 24));
    }, 0);
    const avg = totalDays / responded.length;
    return avg < 1 ? "< 1 day" : `${avg.toFixed(1)} days`;
  })();

  return (
    <div className="space-y-5 w-6xl">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-lg font-bold tracking-tight">Contractor Network</h1>
          <p className="text-xs text-muted-foreground">
            Private contractor directory · {active.length} active · {inactive.length} inactive
          </p>
        </div>
      </div>

      {/* Network KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[
          { label: "Active Partners", value: active.length, color: "text-success" },
          { label: "Opportunities Sent", value: totalOpps, color: "text-primary" },
          { label: "Jobs Awarded", value: totalWon, color: "text-info" },
          { label: "Avg Response", value: avgResponseTime, color: "text-muted-foreground" },
        ].map((s) => (
          <Card key={s.label} className="border shadow-none">
            <CardContent className="p-2.5 text-center">
              <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
              <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-medium">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <QuickViews views={contractorQuickViews} active={filter} onChange={(v) => setFilter(v as FilterTab)} />
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input placeholder="Search contractors…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 h-8 text-xs" />
        </div>
        <BranchRegionFilter value={brFilter} onChange={setBrFilter} contractors={contractors} compact />
      </div>

      {/* Contractor list */}
      <div className="border rounded-lg overflow-hidden">
        <div className="hidden md:grid grid-cols-[2fr_1.2fr_0.8fr_0.8fr_0.6fr_1.2fr_40px] gap-2 bg-muted/40 border-b px-4 py-2.5">
          {["Contractor", "Trade / Area", "Quality", "Response", "Status", "Network Stats", ""].map((h) => (
            <span key={h} className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{h}</span>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="py-8">
            <EmptyState
              icon={Wrench}
              title="No contractors found"
              description={brFilter.branch !== "all" || brFilter.region !== "all" ? "Try adjusting branch/region filters" : "Add contractors to start sending opportunities"}
              compact
            />
          </div>
        ) : (
          filtered.map((c) => {
            const cOpps = opportunities.filter((o) => o.contractorId === c.id);
            const won = cOpps.filter((o) => o.isWinner).length;
            const accepted = cOpps.filter((o) => o.responseStatus === "Accepted").length;
            const rejected = cOpps.filter((o) => o.responseStatus === "Rejected").length;
            const pending = cOpps.filter((o) => ["Pending", "No Response", "Viewed"].includes(o.responseStatus)).length;
            const bids = cOpps.filter((o) => o.responseStatus === "Bid Submitted").length;
            const isExpanded = expandedId === c.id;
            const scorecard = computeScorecard(c, opportunities, jobs, issues, credits);
            const compliance = getCompliance(c);

            return (
              <div key={c.id} className={`border-b last:border-0 ${!c.active ? "opacity-60" : ""}`}>
                <div
                  className="grid grid-cols-1 md:grid-cols-[2fr_1.2fr_0.8fr_0.8fr_0.6fr_1.2fr_40px] gap-2 px-4 py-3 hover:bg-muted/20 cursor-pointer items-center"
                  onClick={() => setExpandedId(isExpanded ? null : c.id)}
                >
                  {/* Name */}
                  <div>
                    <div className="flex items-center gap-1.5">
                      <Link to={`/contractors/${c.id}`} className="font-semibold text-sm hover:text-primary transition-colors">{c.companyName}</Link>
                      <ReliabilityBadge scorecard={scorecard} />
                      {compliance.tone !== "ok" && <AlertTriangle className="h-3 w-3 text-warning shrink-0" />}
                    </div>
                    <p className="text-[11px] text-muted-foreground">{c.contactName} · Since {new Date(c.joinedDate).toLocaleDateString("en-US", { month: "short", year: "numeric" })}</p>
                  </div>
                  {/* Trade / Area */}
                  <div className="text-xs hidden md:block">
                    <div className="flex items-center gap-1 text-muted-foreground"><Wrench className="h-3 w-3 shrink-0" />{c.tradeType}</div>
                    <div className="flex items-center gap-1 text-muted-foreground mt-0.5"><MapPin className="h-3 w-3 shrink-0" />{c.serviceArea}</div>
                    {c.serviceRegions.length > 0 && (
                      <div className="flex items-center gap-1 mt-0.5">
                        {c.serviceRegions.map((r) => (
                          <span key={r} className="text-[9px] bg-muted/60 text-muted-foreground rounded px-1.5 py-0.5">{r}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Quality Rating */}
                  <div className="hidden md:block">
                    <div className="flex items-center gap-0.5">
                      <Star className="h-3 w-3 text-warning fill-warning" />
                      <span className="text-xs font-semibold">{c.rating}</span>
                      <span className="text-[10px] text-muted-foreground">quality</span>
                    </div>
                  </div>
                  {/* Responsiveness */}
                  <div className="hidden md:block">
                    <div className="flex items-center gap-0.5">
                      <Clock className="h-3 w-3 text-info" />
                      <span className="text-xs font-semibold">{c.responsivenessRating}</span>
                      <span className="text-[10px] text-muted-foreground">speed</span>
                    </div>
                  </div>
                  {/* Status */}
                  <div className="hidden md:block">
                    <div className="flex flex-col gap-1">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium border ${
                        c.active ? "bg-success/10 text-success border-success/20" : "bg-muted text-muted-foreground border-border"
                      }`}>
                        {c.active ? "Active" : "Inactive"}
                      </span>
                      {c.active && (
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium border ${
                          c.availabilityStatus === "Available"
                            ? "bg-success/10 text-success border-success/20"
                            : c.availabilityStatus === "Limited"
                            ? "bg-warning/10 text-warning border-warning/20"
                            : "bg-destructive/10 text-destructive border-destructive/20"
                        }`}>
                          {c.availabilityStatus}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Network Stats */}
                  <div className="hidden md:flex items-center gap-2.5 text-[10px]">
                    {cOpps.length > 0 ? (
                      <>
                        <span className="text-muted-foreground font-medium">{cOpps.length} sent</span>
                        {won > 0 && <span className="text-success font-semibold">{won} won</span>}
                        {bids > 0 && <span className="text-info font-medium">{bids} bids</span>}
                        {rejected > 0 && <span className="text-destructive font-medium">{rejected} rej</span>}
                      </>
                    ) : (
                      <span className="text-muted-foreground">New partner</span>
                    )}
                  </div>
                  {/* Expand */}
                  <div className="hidden md:flex justify-center">
                    {isExpanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                  </div>
                </div>

                {/* Expanded detail */}
                {isExpanded && (
                  <div className="px-4 pb-4 border-t bg-muted/[0.04]">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-3">
                      {/* Company Profile */}
                      <div className="space-y-3">
                        <div>
                          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.12em] mb-1.5">Company Profile</p>
                          <div className="space-y-1 text-xs">
                            <div className="flex items-center gap-2 text-muted-foreground"><Phone className="h-3 w-3 shrink-0" />{c.phone}</div>
                            <div className="flex items-center gap-2 text-muted-foreground"><Mail className="h-3 w-3 shrink-0" />{c.email}</div>
                            <div className="flex items-center gap-2 text-muted-foreground"><Wrench className="h-3 w-3 shrink-0" />{c.tradeType}</div>
                            <div className="flex items-center gap-2 text-muted-foreground"><MapPin className="h-3 w-3 shrink-0" />{c.serviceArea}</div>
                            {c.maxWeeklyJobs > 0 && (
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <Briefcase className="h-3 w-3 shrink-0" />
                                Capacity: {c.maxWeeklyJobs} jobs/week
                              </div>
                            )}
                            {c.serviceZipCodes.length > 0 && (
                              <div className="mt-1.5">
                                <p className="text-[10px] text-muted-foreground mb-1">Service Zip Codes</p>
                                <div className="flex flex-wrap gap-1">
                                  {c.serviceZipCodes.map((z) => (
                                    <span key={z} className="text-[9px] bg-muted/60 text-muted-foreground rounded px-1.5 py-0.5 font-mono">{z}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        <div>
                          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.12em] mb-1.5">Ratings</p>
                          <div className="space-y-1">
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-muted-foreground">Quality</span>
                              <RatingStars rating={c.rating} />
                            </div>
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-muted-foreground">Responsiveness</span>
                              <RatingStars rating={c.responsivenessRating} />
                            </div>
                          </div>
                        </div>

                        {c.notes && (
                          <div>
                            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.12em] mb-1">Internal Notes</p>
                            <p className="text-xs text-muted-foreground italic leading-relaxed">{c.notes}</p>
                          </div>
                        )}
                      </div>

                      {/* Compliance & Insurance */}
                      <div className="space-y-3">
                        <div>
                          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.12em] mb-1.5">Compliance & Insurance</p>
                          <div className="space-y-1.5">
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-muted-foreground flex items-center gap-1"><Shield className="h-3 w-3" /> Insurance</span>
                              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium border ${
                                c.insuranceOnFile
                                  ? insuranceExpiring
                                    ? "bg-warning/10 text-warning border-warning/20"
                                    : "bg-success/10 text-success border-success/20"
                                  : "bg-destructive/10 text-destructive border-destructive/20"
                              }`}>
                                {c.insuranceOnFile ? (insuranceExpiring ? "Expiring Soon" : "On File") : "Missing"}
                              </span>
                            </div>
                            {c.insuranceExpiry && (
                              <p className="text-[10px] text-muted-foreground ml-4">Expires: {c.insuranceExpiry}</p>
                            )}
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-muted-foreground flex items-center gap-1"><FileText className="h-3 w-3" /> W-9</span>
                              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium border ${
                                c.w9OnFile ? "bg-success/10 text-success border-success/20" : "bg-warning/10 text-warning border-warning/20"
                              }`}>
                                {c.w9OnFile ? "On File" : "Missing"}
                              </span>
                            </div>
                            {c.licenseNumber && (
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-muted-foreground">License</span>
                                <span className="font-mono text-[10px]">{c.licenseNumber}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        {c.complianceNotes && (
                          <div>
                            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.12em] mb-1">Compliance Notes</p>
                            <p className="text-[11px] text-muted-foreground italic leading-relaxed">{c.complianceNotes}</p>
                          </div>
                        )}

                        {/* Performance Summary */}
                        <div>
                          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.12em] mb-1.5">Performance</p>
                          <div className="grid grid-cols-2 gap-1.5">
                            {[
                              { label: "Sent", value: cOpps.length, icon: Briefcase },
                              { label: "Won", value: won, icon: CheckCircle },
                              { label: "Bids", value: bids, icon: DollarSign },
                              { label: "Rejected", value: rejected, icon: XCircle },
                            ].map((s) => (
                              <div key={s.label} className="flex items-center gap-1.5 p-1.5 rounded border bg-muted/30 text-xs">
                                <s.icon className="h-3 w-3 text-muted-foreground shrink-0" />
                                <span className="font-semibold">{s.value}</span>
                                <span className="text-muted-foreground text-[10px]">{s.label}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Opportunity History */}
                      <div>
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.12em] mb-2">Opportunity History</p>
                        {cOpps.length === 0 ? (
                          <p className="text-xs text-muted-foreground">No opportunities sent yet.</p>
                        ) : (
                          <div className="border rounded-md overflow-hidden">
                            <table className="w-full text-[11px]">
                              <thead>
                                <tr className="bg-muted/40 border-b">
                                  <th className="text-left py-1.5 px-2 font-semibold text-muted-foreground uppercase text-[9px] tracking-wider">Job</th>
                                  <th className="text-left py-1.5 px-2 font-semibold text-muted-foreground uppercase text-[9px] tracking-wider">Sent</th>
                                  <th className="text-center py-1.5 px-2 font-semibold text-muted-foreground uppercase text-[9px] tracking-wider">
                                    <Eye className="h-3 w-3 mx-auto" />
                                  </th>
                                  <th className="text-left py-1.5 px-2 font-semibold text-muted-foreground uppercase text-[9px] tracking-wider">Status</th>
                                  <th className="text-right py-1.5 px-2 font-semibold text-muted-foreground uppercase text-[9px] tracking-wider">Bid</th>
                                </tr>
                              </thead>
                              <tbody>
                                {cOpps.map((opp) => {
                                  const job = jobs.find((j) => j.id === opp.jobId);
                                  return (
                                    <tr key={opp.id} className={`border-b last:border-0 hover:bg-muted/20 ${opp.isWinner ? "bg-success/[0.04]" : ""}`}>
                                      <td className="py-1.5 px-2">
                                        <Link to={`/jobs/${opp.jobId}`} className="text-primary font-medium hover:underline text-[10px]">{opp.jobId}</Link>
                                        {opp.isWinner && <span className="ml-1 text-[8px] bg-success/15 text-success rounded px-1 py-0.5 font-bold uppercase">Won</span>}
                                      </td>
                                      <td className="py-1.5 px-2 text-muted-foreground text-[10px]">{opp.sentDate}</td>
                                      <td className="py-1.5 px-2 text-center">
                                        {opp.viewedDate ? (
                                          <Eye className="h-3 w-3 text-success mx-auto" />
                                        ) : (
                                          <span className="text-[10px] text-muted-foreground">—</span>
                                        )}
                                      </td>
                                      <td className="py-1.5 px-2"><StatusBadge type="contractorResponse" value={opp.responseStatus} /></td>
                                      <td className="py-1.5 px-2 text-right font-medium">{opp.bidAmount ? `$${opp.bidAmount.toLocaleString()}` : "—"}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
